import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { IDisplayModeValueEditor, IEditModeValueEditor } from "./Common";

import { FileUploadLayer } from "../BuilderUI/FileUploadLayer";
import { FileData } from "../../rule-engine-core/Common/FileData";
import { StyledButton } from "../BuildlingBlocks/StyledButton";
import { UploadIcon } from "../Icon/UploadIcon";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

const ListFileValueRender = ({ values, length = 15 }: { values: FileData[]; length?: number }) => {
    let requiredLength: boolean = false;
    let renderValue = values.reduce((s, current, index) => {
        if (index === 0) return `${current?.fileName || ""}`;
        if (`${s}, ${current}`.length > length) {
            requiredLength = true;
            return s;
        }
        if (current?.fileName) {
            s = `${s}, ${current?.fileName}`;
        }
        return s;
    }, "");

    if (renderValue === "") {
        return (
            <StyledButton
                variant="contained"
                sx={{ background: UIStyleConstants.BLUE_COLOR }}
                startIcon={<UploadIcon />}
                data-testid="btn-xFileUpload-upload"
                size="small"
            >
                Upload
            </StyledButton>
        );
    }

    return (
        <>
            {renderValue} {requiredLength ? `... (${values.length})` : ``}
        </>
    );
};

export const DisplayModeFileValueEditor = ({ value }: IDisplayModeValueEditor) => {
    if (Array.isArray(value)) {
        return (
            <div
                className="ValueEditor-display"
                data-testid="ValueEditor-File-Display"
            >
                <ListFileValueRender values={value} />
            </div>
        );
    }

    if (value) {
        return (
            <div
                className="ValueEditor-display"
                data-testid="ValueEditor-File-Display"
            >
                {`${value?.file?.name} [${value?.file?.size / 1000} kb]` || `${value?.fileName}`}
            </div>
        );
    }

    return (
        <div
            className="ValueEditor-display"
            data-testid="ValueEditor-File-Display"
        >
            <StyledButton
                variant="contained"
                sx={{ background: UIStyleConstants.BLUE_COLOR }}
                startIcon={<UploadIcon />}
                data-testid="btn-xFileUpload-upload"
                size="small"
            >
                Upload
            </StyledButton>
        </div>
    );
};

export const EditModeFileValueEditor = React.forwardRef<HTMLElement, IEditModeValueEditor>(
    // eslint-disable-next-line
    ({ value, forData, onUpdate }: IEditModeValueEditor, ref) => {
        let values = forData.list
            ? Array.isArray(value)
                ? value
                : value
                  ? [value]
                  : []
            : Array.isArray(value)
              ? value[0]
              : value;

        const onValueUpdate = (newValues: FileData[]) => {
            onUpdate(newValues);
        };

        if (forData.list) {
            return (
                <EditorFileList
                    values={values}
                    updateCallback={onValueUpdate}
                />
            );
        }

        const addFiles = (files: FileData[]) => {
            onUpdate(files[0]);
        };

        // eslint-disable-next-line
        const deleteFile = (_file: FileData, _index: number) => {
            onUpdate(undefined);
        };

        return (
            <div
                className="ValueEditor-display"
                data-testid="ValueEditor-File-Edit"
            >
                <FileUploadLayer
                    files={values ? [values] : []}
                    addFiles={addFiles}
                    onDelete={deleteFile}
                    showFileName={true}
                    showFileSize={true}
                    showPreview={true}
                    filesLimit={1}
                />
            </div>
        );
    },
);

export interface EditorFileListProps {
    values: AppGlueAny[];
    updateCallback: (vals: AppGlueAny[]) => void;
}

export const EditorFileList: React.FC<EditorFileListProps> = (props: EditorFileListProps) => {
    const [values, setValues] = useState<AppGlueAny[]>(props.values);

    useEffect(() => {
        setValues([...props.values]);
    }, [props.values, setValues]);

    const update = (vals: FileData[]) => {
        props.updateCallback(vals);

        setValues([...vals]);
    };

    const addFiles = (files: FileData[]) => {
        update([...values, ...files]);
    };

    const deleteFile = (_file: FileData, index: number) => {
        values.splice(index, 1);
        update(values);
    };

    return (
        <StyledFileList
            className="PropertyList-Wrapper FileList-Wrapper"
            data-testid="ValueEditor-FileList-Edit"
        >
            <FileUploadLayer
                files={values || []}
                addFiles={addFiles}
                onDelete={deleteFile}
                showFileName={true}
                showFileSize={true}
                showPreview={true}
                filesLimit={100}
            />
        </StyledFileList>
    );
};

const StyledFileList = styled.div`
    width: 100%;

    text-align: right;

    .FileList {
        display: flex;
        flex-direction: column;

        > div {
            margin-top: 5px;

            &:first-child {
                margin-top: 0;
            }
        }

        .MuiSwitch-root {
            display: flex;
        }
    }

    .FileList-Label {
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        font-style: normal;
        font-weight: 600;
        margin-bottom: 5px;
    }

    .FileList-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
`;
